import React, { useEffect, useState, useRef } from "react";
import "./Organization.css";
import { Button, Text, DataTable, TextInput, Box } from "grommet";
import CommonButton from "../../../common/Button/CommonButton";
import CommonSearchInput from "../../../common/SearchInput/CommonSearchInput";
import { useStore } from "../../../../../../Hooks/useStore";
import createFlightService from "../Users/CreateFlight/create-flight.service";
import EditIcon from "./Editicon1";
import AddOrganization from "./AddOrganization";
import { observer } from "mobx-react";
import EditOrganization from "./EditOrganization";
import { format } from "date-fns";
import searchIcon from "../../../../../../assets/search.svg";
import ViewIcon from "../../../../../../assets/visibility.svg";
import AddIcon from "../../../../../../assets/add1.svg";
import airplane from "../../../../../../assets/airplanemode_active (1).svg";
import { Link, useNavigate } from "react-router-dom";
import Pagination from "../../../common/Pagination/Pagination";
import { APIs } from "../../../../../../util/api/APIs";
import SafeairManager from "../../../../../../util/api/SafeairManager";
import userActionDropdown from "../../../../../../assets/arrow_drop_down (1).svg";
import noResult from "../../../../../../assets/no-result.png";
function Organization() {
  const { rootStore } = useStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchString, setSearchString] = useState("");
  const [originalOrgList, setOriginalOrgList] = useState([]);
  const [originalSubscriptions, setOriginalSubscriptions] = useState([]);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [isLoading, setIsLoading] = useState(false);

  const statusOptions = ["All Status", "ACTIVE", "CANCELLED", "EXPIRED", "RENEWED"];
  const navigation = useNavigate();
  
  // Calculate total items and pages based on current data
  const totalItems = selectedStatus === "All Status" 
    ? rootStore.organizationStore.getOrganizationList.length 
    : subscriptions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Fetch organizations when status is "All Status"
  useEffect(() => {
    if (selectedStatus === "All Status") {
      fetchOrganizations();
    }
  }, [selectedStatus, rootStore.organizationStore.getupdate]);

  // Fetch subscriptions when status is not "All Status"
  useEffect(() => {
    if (selectedStatus !== "All Status") {
      fetchSubscriptions(selectedStatus);
    }
  }, [selectedStatus]);

  const fetchOrganizations = async () => {
    try {
      setIsLoading(true);
      let organizations = await createFlightService.getOrganizations();
      rootStore.organizationStore.setOrganizationList(organizations);
      setOriginalOrgList(organizations);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubscriptions = async (status) => {
    try {
      setIsLoading(true);
      const result = await SafeairManager.getAllSubscriptions({
        api: APIs.Form.getAllSubscriptions,
        queryParams: { status }
      });
      setSubscriptions(result);
      setOriginalSubscriptions(result);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (status) => {
    setSelectedStatus(status);
    setDropdownOpen(false);
    setCurrentPage(1); // Reset to first page when status changes
    setSearchString(""); // Reset search string
  };

  // Handle search for both organizations and subscriptions
  const onSearch = (value) => {
    setSearchString(value);
    
    if (selectedStatus === "All Status") {
      const filteredOrgs = originalOrgList.filter(org => 
        org.name?.toLowerCase().includes(value.toLowerCase()) ||
        org.address?.toLowerCase().includes(value.toLowerCase()) ||
        org.phoneNumbers?.some(phone => phone.toLowerCase().includes(value.toLowerCase())) ||
        org.emails?.some(email => email.toLowerCase().includes(value.toLowerCase()))
      );
      rootStore.organizationStore.setOrganizationList(filteredOrgs);
    } else {
      const filteredSubs = originalSubscriptions.filter(sub => 
        sub.organization?.name?.toLowerCase().includes(value.toLowerCase()) ||
        sub.organization?.address?.toLowerCase().includes(value.toLowerCase()) ||
        sub.organization?.phoneNumbers?.some(phone => phone.toLowerCase().includes(value.toLowerCase())) ||
        sub.organization?.emails?.some(email => email.toLowerCase().includes(value.toLowerCase()))
      );
      setSubscriptions(filteredSubs);
    }
    
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSearch(searchString);
    }
  };

  // Get current items based on pagination
  const getCurrentItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    if (selectedStatus === "All Status") {
      return rootStore.organizationStore.getOrganizationList.slice(startIndex, endIndex);
    } else {
      return subscriptions.slice(startIndex, endIndex);
    }
  };

  const column6 = [
    {
      property: "legalEntryName",
      header: <Text style={{textAlign:"center"}}>Legal Entry Name</Text>,
      primary: true,
      render: (item) => <div>{selectedStatus === "All Status" ? item.name : item.organization?.name}</div>,
    },
    {
      property: "address",
      header: <Text style={{textAlign:"center"}}>Address</Text>,
      primary: true,
      render: (item) => <div>{selectedStatus === "All Status" ? item.address : item.organization?.address}</div>,
    },
    {
      property: "phoneNumber",
      header: <Text style={{textAlign:"center"}}>Phone Number</Text>,
      primary: true,
      render: (item) => (
        <div style={{ whiteSpace: "break-spaces" }}>
          {selectedStatus === "All Status" 
            ? item.phoneNumbers?.join("\n") 
            : item.organization?.phoneNumbers?.join("\n")}
        </div>
      ),
    },
    {
      property: "email",
      header: <Text style={{textAlign:"center"}}>Email</Text>,
      primary: true,
      render: (item) => (
        <div style={{ whiteSpace: "break-spaces" }}>
          {selectedStatus === "All Status" 
            ? item.emails?.join("\n") 
            : item.organization?.emails?.join("\n")}
        </div>
      ),
    },
    {
      property: "date",
      header: <Text style={{textAlign:"center"}}>Date</Text>,
      primary: true,
      render: (item) => {
        const dateToFormat = selectedStatus === "All Status" ? item.timestamp : item.createdAt;
        let formattedDate;
        if (dateToFormat != null) {
          formattedDate = format(new Date(dateToFormat), "MM/dd/yyyy");
        } else {
          formattedDate = "";
        }
        return <div>{formattedDate}</div>;
      },
    },
    {
      property: "action",
      header: <Text style={{ textAlign: "center" }}>Action</Text>,
      primary: true,
      render: (item) => (
        <div style={{ display: "flex", alignSelf: "center" }}>
          <div onClick={() => toggleEditForm(selectedStatus === "All Status" ? item.id : item.organization?.id)}>
            <EditIcon user={item} />
          </div>
          <div>
            <div style={{ alignSelf: "center", marginLeft: "15px", cursor: "pointer" }}>
              <div
                className="airplane"
                onClick={() => {
                  const orgId = selectedStatus === "All Status" ? item.id : item.organization?.id;
                  rootStore.flightsStore.handleUserOId(orgId);
                  navigation("/flights");
                }}
              >
                <img style={{ width: 17, height: 17 }} src={airplane} />
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      property: "action",
      header: <Text>Subscription</Text>,
      primary: true,
      render: (item) => (
        <div style={{ display: "flex", alignSelf: "center" }}>
          <div onClick={() => {
            const orgId = selectedStatus === "All Status" ? item.id : item.organization?.id;
            navigation(`/addsubscription?${orgId}`);
          }}>
            <EditIcon user={item} />
          </div>
          <div>
            <div style={{ alignSelf: "center", marginLeft: "15px", cursor: "pointer" }}>
              <div
                className="airplane"
                onClick={() => {
                  const orgId = selectedStatus === "All Status" ? item.id : item.organization?.id;
                  rootStore.flightsStore.handleUserOId(orgId);
                  navigation(`/viewsubscription?${orgId}`);
                }}
              >
                <img style={{ width: 17, height: 17 }} src={ViewIcon} />
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const toggleEditForm = (organizationId) => {
    try {
      rootStore.organizationStore.handlelegalEntryName("");
      rootStore.organizationStore.handleAdress("");
      rootStore.organizationStore.handlePhoneNumber("");
      rootStore.organizationStore.handleEmail("");
      rootStore.organizationStore.handleEditOrganizationbar(true);

      createFlightService
        .getOrganization(organizationId)
        .then((organizationResponse) => {
          rootStore.organizationStore.handleid(organizationResponse.id);
          rootStore.organizationStore.handlelegalEntryName(organizationResponse.name);
          rootStore.organizationStore.handleAdress(organizationResponse.address);
          rootStore.organizationStore.handlePhoneNumber(organizationResponse.phoneNumbers);
          rootStore.organizationStore.handleEmail(organizationResponse.emails);

          setIsEditFormOpen(!isEditFormOpen);
          rootStore.organizationStore.handleEditOrganizationbar(organizationResponse);
        });
    } catch (error) {
      console.error("Error fetching organization details:", error);
    }
  };


  // Handle outside click
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false); // Close the dropdown
      }
    };

    // Add event listener for clicks
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box className="organization-screen-container">
      <div className="organization-screen-header">
        <div className="organization-screen-left-header">
          <div style={{ position: "relative" }}>
            <CommonSearchInput
              style={{
                padding: "8px 8px 8px 35px",
                borderRadius: "10px",
                background: "#F2F8FF",
                outline: "0",
                border: "none",
                color: "#656566",
                fontFamily: "Inter",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "18px",
                height: "40px",
                maxWidth: "320px",
                width: "320px",
              }}
              placeholder="Search"
              value={searchString}
              onchange={(e) => setSearchString(e.target.value)}
              onkeydown={handleKeyDown}
            />
            <img
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "16px",
                height: "16px",
              }}
              src={searchIcon}
              className="searchIcon"
            />
          </div>

          <CommonButton
            title="Search"
            onclick={() => onSearch(searchString)}
            padding="12px 24px 12px 28px"
            color="#fff"
            bgColor="#357EFF"
            radius={10}
            border="1px solid #357EFF"
            lineheight="14px"
            ml={5}
          />
        </div>

        <div className="user-screen-right-header">
          <div style={{ maxHeight: "40px", position: "relative" }}>
            <div
              style={{
                display: "flex",
                width: "148px",
                padding: "8px 8px 8px 12px",
                justifyContent: "space-between",
                alignItems: "center",
                borderRadius: "8px",
                border: "1px solid rgb(237 237 235)",
                color: "#4B628A",
                fontFamily: "Inter",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "520",
                lineHeight: "20px",
                cursor: "pointer",
              }}
              className="users-action-btn"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {selectedStatus}
              <img
                style={{
                  width: "24px",
                  height: "24px",
                  flexShrink: "0",
                  transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
                src={userActionDropdown}
                alt="Dropdown Icon"
              />
            </div>

            {dropdownOpen && (
              <div
                ref={dropdownRef}
                className="dropdown-menu"
                style={{
                  position: "absolute",
                  top: "100%",
                  marginTop: "4px",
                  width: "148px",
                  backgroundColor: "#fff",
                  border: "1px solid #ddd",
                  zIndex: "1",
                  borderTopRightRadius: "10px",
                  borderTopLeftRadius: "10px",
                  fontSize: "14px",
                  padding: "8px 0",
                }}
              >
                {statusOptions.map((status) => (
                  <p
                    key={status}
                    className="pointer"
                    onClick={() => handleSelect(status)}
                    style={{
                      margin: 0,
                      padding: "6px 12px",
                      cursor: "pointer",
                      backgroundColor: selectedStatus === status ? "#f1f1f1" : "transparent",
                    }}
                  >
                    {status}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="organization-screen-right-header">
          <Button
            label="Add Organization"
            onClick={() => rootStore.organizationStore.handleAddOrganizationbar(true)}
            style={{
              display: "flex",
              padding: "12px 24px 12px 28px",
              justifyContent: "flex-end",
              alignItems: "center",
              alignSelf: "stretch",
              borderRadius: "10px",
              border: "1px solid #357EFF",
              background: "#357EFF",
              color: "#FFF",
              fontFamily: "Inter",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: "600",
              lineHeight: "16px",
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <>
         <div style={{ overflow: "auto", marginTop: 16, position: 'relative', minHeight: '200px' }}>
  <DataTable
    columns={column6}
    data={getCurrentItems()}
    resizeable
    step={itemsPerPage}
    className="organization-table"
  />
  
  {getCurrentItems().length === 0 && (
    <div style={{
      position: 'absolute',
      top: '62%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center',
      width: '100%'
    }}>
      <img 
        src={noResult} // Make sure to import your image
        alt="No data available"
        style={{ 
          width: '120px',
          height: '120px',
          marginBottom: '16px'
        }}
      />
      <p style={{
        color: '#666',
        fontSize: '16px',
        fontWeight: 500,
        margin: 0
      }}>
        No Data Available
      </p>
    </div>
  )}
</div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              onPageChange={(page) => setCurrentPage(page)}
            />
          )}
        </>
      )}

      {rootStore.organizationStore.getOrganizationbarShow && <AddOrganization />}
      {rootStore.organizationStore.getEditOrganizationbarShow && <EditOrganization />}
    </Box>
  );
}

export default observer(Organization);